import type { RouteKey } from '../i18n/routes'

export type RouteDisposition = 'route' | 'redirect' | 'modal' | 'inactive'

export type HistoricalRoute = {
  legacyPath: string
  disposition: RouteDisposition
  canonicalRoute?: RouteKey
  note: string
}

const historicalVehiclePaths = [
  '/scheda-veicolo/302/109/lancia-1-0-firefly-hybrid-unyca-s-s-70cv',
  '/scheda-veicolo/321/109/kia-1-6-crdi-mhev-style-dct',
  '/scheda-veicolo/388/109/isuzu-tt-passo-e-ribaltabile-trilaterale',
  '/scheda-veicolo/467/109/jeep-1-2-t-summit-fwd-100cv',
  '/scheda-veicolo/523/109/bmw-320d-mhev-48v-xdrive-auto',
  '/scheda-veicolo/570/109/isuzu-tt',
  '/scheda-veicolo/587/109/audi-2-0-tdi-mhev-s-line-ed-quattro-204cv-s-tronic',
  '/scheda-veicolo/596/109/bmw-xdrive18d-xline-auto-my18',
  '/scheda-veicolo/599/109/fiat-1-2-hybrid-la-prima-110cv-auto',
  '/scheda-veicolo/601/109/jeep-1-5-turbo-t4-mhev-limited-2wd-130cv-dct',
  '/scheda-veicolo/607/109/audi-40-2-0-tdi-mhev-12v-quattro-204cv-s-tronic',
  '/scheda-veicolo/618/109/jeep-1-2-t-altitude-fwd-100cv',
  '/scheda-veicolo/628/109/mercedes-311-2-1-cdi-f-39-33-fwd-e6',
  '/scheda-veicolo/629/109/dacia-1-8-hybrid-extreme-155cv-auto',
  '/scheda-veicolo/634/109/bmw-m-550d-xdrive-auto',
  '/scheda-veicolo/641/109/citroen-1-2-puretech-turbo-plus-100cv-s-s',
  '/scheda-veicolo/643/109/fiat-1-2-hybrid-la-prima-110cv-auto',
  '/scheda-veicolo/646/109/hyundai-i30-wagon-1-0-t-gdi-prime-48v-120cv',
  '/scheda-veicolo/665/109/audi-2-0-tdi-mhev-s-line-edition-204cv-s-tronic',
  '/scheda-veicolo/669/109/nissan-1-3-mhev-n-connecta-2wd-158cv-xtronic',
  '/scheda-veicolo/686/109/volkswagen-2-0-tdi-4motion-190cv-dsg-7m',
  '/scheda-veicolo/691/109/opel-1-2-elegance-s-s-130cv-at6',
  '/scheda-veicolo/692/109/volkswagen-1-5-tsi-style-dsg',
] as const

/** Complete 37-URL sitemap observed 2026-09-26, plus its planned disposition. */
export const historicalRoutes: readonly HistoricalRoute[] = [
  { legacyPath: '/', disposition: 'redirect', canonicalRoute: 'home', note: 'Legacy root redirected to /home in the audit.' },
  { legacyPath: '/assistenza', disposition: 'redirect', canonicalRoute: 'vehicles', note: 'Redirect to catalogue observed in audit.' },
  { legacyPath: '/auto-storiche', disposition: 'redirect', canonicalRoute: 'vehicles', note: 'Duplicate catalogue; preserve no separate listing.' },
  { legacyPath: '/chi-siamo', disposition: 'redirect', canonicalRoute: 'contacts', note: 'Removed company page; preserve the legacy URL through the contact flow.' },
  { legacyPath: '/contatti', disposition: 'route', canonicalRoute: 'contacts', note: 'Contact page.' },
  { legacyPath: '/faq', disposition: 'route', canonicalRoute: 'faq', note: 'FAQ page.' },
  { legacyPath: '/noleggio-breve', disposition: 'route', canonicalRoute: 'shortRental', note: 'Service page.' },
  { legacyPath: '/noleggio-lungo', disposition: 'inactive', note: 'Audit labels service as not active; keep out of navigation and sitemap.' },
  { legacyPath: '/parco-auto', disposition: 'redirect', canonicalRoute: 'vehicles', note: 'Legacy catalogue slug.' },
  { legacyPath: '/prenotazione-online', disposition: 'route', canonicalRoute: 'booking', note: 'Booking request page, not a booking-provider claim.' },
  { legacyPath: '/privacy', disposition: 'route', canonicalRoute: 'privacy', note: 'Legal page.' },
  { legacyPath: '/richiedi-auto', disposition: 'redirect', canonicalRoute: 'vehicles', note: 'Redirect to catalogue observed in audit.' },
  { legacyPath: '/servizi', disposition: 'route', canonicalRoute: 'services', note: 'Services page.' },
  { legacyPath: '/trade-in', disposition: 'redirect', canonicalRoute: 'sellVehicle', note: 'Consolidate valuation request under sale-your-car.' },
  ...historicalVehiclePaths.map((legacyPath) => ({ legacyPath, disposition: 'redirect' as const, canonicalRoute: 'vehicle' as const, note: 'Resolve only if a current repository record exists; otherwise show an unavailable vehicle state.' })),
] as const

/** UI-only intents; these are not URLs, canonical pages or sitemap entries. */
export const modalIntents = ['contact', 'vehicle-enquiry', 'filters'] as const

/** Links observed in navigation but without independently verified public content. */
export const inactiveNavigationItems = ['TS Garage', 'Rivenditore Certificato'] as const

if (historicalRoutes.length !== 37) throw new Error('Historical sitemap contract must contain 37 routes.')
